import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { PROPOSTAS_DESIGN, getCorAvatar, getIniciais } from './Propostas.design';
import { caminhoEdicao, isOrcamento } from './propostasRotas';

const D = PROPOSTAS_DESIGN;

function formatCurrency(value) {
  if (value === undefined || value === null) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
}

export default function KanbanBoard({ propostas, estagios, onStatusChange, onShare, onAcompanhar }) {
  const navigate = useNavigate();
  const dragItem = useRef(null);
  const [arrastando, setArrastando] = useState(null);
  const [colunaAlvo, setColunaAlvo] = useState(null);

  const colunas = useMemo(() => {
    const ordenados = [...(estagios || [])].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    return ordenados.map(estagio => ({
      ...estagio,
      itens: propostas.filter(p => p.status === estagio.status)
    }));
  }, [estagios, propostas]);

  const handleDragStart = (proposta) => {
    dragItem.current = proposta;
    setArrastando(proposta._id);
  };

  const handleDragEnd = () => {
    dragItem.current = null;
    setArrastando(null);
    setColunaAlvo(null);
  };

  const handleDragOver = (e, colunaId) => {
    e.preventDefault();
    if (colunaAlvo !== colunaId) setColunaAlvo(colunaId);
  };

  const handleDrop = (estagio) => {
    const proposta = dragItem.current;
    handleDragEnd();
    if (!proposta || !estagio.status || proposta.status === estagio.status) return;
    onStatusChange(proposta._id, estagio.status);
  };

  if (!colunas.length) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center bg-white dark:bg-[#222529] rounded-2xl border border-gray-100 dark:border-[#383838]">
        <div className="w-16 h-16 rounded-2xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center mb-4 text-[#7C3AED]">
          <Iconify icon="mdi:view-column-outline" width={28} />
        </div>
        <p className="text-lg font-bold text-gray-900 dark:text-[#D1D2D3] mb-1.5">Nenhum estágio configurado</p>
        <p className="text-sm text-gray-500 dark:text-[#ABABAD] max-w-sm mb-4">
          Crie estágios para organizar as propostas em um funil de negociação.
        </p>
        <button
          onClick={() => navigate('/dashboard/configuracoes/propostas-config')}
          className="text-sm font-medium text-[#7C3AED] hover:underline"
        >
          Configurar estágios
        </button>
      </div>
    );
  }

  return (
    <div className={D.kanban.board}>
      {colunas.map(coluna => {
        const colunaId = coluna._id || coluna.nome;
        const total = coluna.itens.reduce((acc, p) => acc + (p.valor_total || 0), 0);
        const cor = coluna.cor || '#7C3AED';

        return (
          <div
            key={colunaId}
            onDragOver={(e) => handleDragOver(e, colunaId)}
            onDrop={() => handleDrop(coluna)}
            className={D.kanban.column}
          >
            <div className={D.kanban.columnHeader}>
              <div className={D.kanban.columnIconBox} style={{ backgroundColor: `${cor}20` }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cor }} />
              </div>
              <p className={D.kanban.columnTitle}>{coluna.nome}</p>
              <span className={D.kanban.columnCount}>{coluna.itens.length}</span>
            </div>

            {total > 0 && <p className={D.kanban.columnTotal}>{formatCurrency(total)}</p>}

            <div
              className={`${D.kanban.columnBody} ${colunaAlvo === colunaId ? D.kanban.columnBodyDragging : ''}`}
            >
              {coluna.itens.map(proposta => {
                const apoio =
                  proposta.lead_id?.nome ||
                  proposta.vendedor_id?.nomeFantasia ||
                  proposta.vendedor_id?.nome;

                return (
                  <div
                    key={proposta._id}
                    draggable
                    onDragStart={() => handleDragStart(proposta)}
                    onDragEnd={handleDragEnd}
                    onClick={() => navigate(caminhoEdicao(proposta))}
                    className={`${D.kanbanCard.container} ${
                      arrastando === proposta._id ? D.kanbanCard.containerDragging : ''
                    }`}
                  >
                    <div className={D.kanbanCard.header}>
                      <div
                        className={D.kanbanCard.avatar}
                        style={{ backgroundColor: getCorAvatar(proposta.titulo) }}
                      >
                        {getIniciais(proposta.titulo)}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className={D.kanbanCard.title}>
                          {isOrcamento(proposta) && (
                            <Iconify
                              icon="mdi:receipt-text-outline"
                              width={11}
                              title="Orçamento"
                              className="inline-block mr-1 -mt-0.5 text-gray-400"
                            />
                          )}
                          {proposta.assinatura_cliente?.assinado_em && (
                            <Iconify
                              icon="mdi:draw-pen"
                              width={11}
                              title="Assinada pelo cliente"
                              className="inline-block mr-1 -mt-0.5 text-green-600 dark:text-green-400"
                            />
                          )}
                          {proposta.titulo}
                        </p>
                        {apoio && (
                          <p className={D.kanbanCard.subtitle}>
                            <Iconify
                              icon={proposta.lead_id?.nome ? 'mdi:handshake-outline' : 'mdi:account-outline'}
                              width={10}
                              className="flex-shrink-0"
                            />
                            <span className="truncate">{apoio}</span>
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        {onAcompanhar && (
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); onAcompanhar(proposta); }}
                            title="Acompanhamento do cliente"
                            className={D.kanbanCard.action}
                          >
                            <Iconify icon="mdi:chart-timeline-variant" width={12} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); onShare(proposta); }}
                          title="Compartilhar / baixar PDF"
                          className={D.kanbanCard.action}
                        >
                          <Iconify icon="mdi:share-variant-outline" width={12} />
                        </button>
                      </div>
                    </div>

                    <div className={D.kanbanCard.footer}>
                      <div className={D.kanbanCard.meta}>
                        <span className={D.kanbanCard.date}>{formatDate(proposta.created_at)}</span>
                        {proposta.aberturas_count > 0 && (
                          <span
                            className={D.kanbanCard.views}
                            title={`${proposta.aberturas_count} visualizações`}
                          >
                            <Iconify icon="mdi:eye-outline" width={10} />
                            {proposta.aberturas_count}
                          </span>
                        )}
                      </div>
                      <span className={D.kanbanCard.value}>{formatCurrency(proposta.valor_total)}</span>
                    </div>
                  </div>
                );
              })}

              {coluna.itens.length === 0 && colunaAlvo !== colunaId && (
                <div className={D.kanban.columnEmpty}>
                  <Iconify icon="eva:inbox-outline" width={24} className="text-gray-300 mb-1.5" />
                  <p className={D.kanban.columnEmptyText}>Arraste propostas para cá</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
